import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  LayoutDashboard,
  FileText,
  Users,
  UserCheck,
  Landmark,
  Clock,
  Wallet,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Building2,
  Settings,
  MessageSquare,
  PanelLeftClose,
  PanelLeft,
  Receipt,
  CreditCard,
  TrendingUp
} from 'lucide-react';

const sidebarItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/enquiries', icon: MessageSquare, label: 'Enquiries' },
  { path: '/loan-files', icon: FileText, label: 'Loan Files' },
  { path: '/customers', icon: Users, label: 'Customers' },
  { path: '/employees', icon: UserCheck, label: 'Employees', adminOnly: true },
  { path: '/bank-agents', icon: Landmark, label: 'Bank Agents' },
  { path: '/payments', icon: CreditCard, label: 'Payments', adminOnly: true },
  { path: '/expenses', icon: Receipt, label: 'Expenses', adminOnly: true },
  { path: '/financials', icon: TrendingUp, label: 'Financials', adminOnly: true },
  { path: '/attendance', icon: Clock, label: 'Attendance', adminOnly: true },
  { path: '/salary', icon: Wallet, label: 'Salary', adminOnly: true },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

const SidebarLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get initial state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Save to localStorage when changed
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
  }, [isCollapsed]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-background">
        {/* Sidebar */}
        <aside 
          className={cn(
            "bg-brand-navy text-white flex flex-col fixed h-screen left-0 top-0 z-50 transition-all duration-300 ease-in-out",
            isCollapsed ? "w-[70px]" : "w-64"
          )}
        >
          {/* Logo */}
          <div className={cn(
            "p-4 border-b border-brand-navy-light flex items-center",
            isCollapsed ? "justify-center" : "justify-between"
          )}>
            <div className={cn(
              "flex items-center gap-3",
              isCollapsed && "justify-center"
            )}>
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-xl font-bold text-brand-navy font-heading">A</span>
              </div>
              {!isCollapsed && (
                <div className="overflow-hidden">
                  <h1 className="font-bold text-white font-heading tracking-tight text-sm">Atharva Finance</h1>
                  <p className="text-xs text-brand-gold">DSA Management</p>
                </div>
              )}
            </div>
            {!isCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className="text-slate-400 hover:text-white hover:bg-brand-navy-light h-8 w-8"
                data-testid="collapse-sidebar-btn"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Expand button when collapsed */}
          {isCollapsed && (
            <div className="p-2 flex justify-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className="text-slate-400 hover:text-white hover:bg-brand-navy-light h-8 w-8"
                data-testid="expand-sidebar-btn"
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className={cn("space-y-1", isCollapsed ? "px-2" : "px-3")}>
              {sidebarItems
                .filter(item => !item.adminOnly || isAdmin)
                .map((item) => {
                const isActive = location.pathname === item.path;
                
                const navContent = (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                    className={cn(
                      "flex items-center gap-3 rounded-lg transition-all duration-200 group",
                      isCollapsed ? "px-3 py-3 justify-center" : "px-4 py-3",
                      isActive
                        ? "bg-brand-gold text-brand-navy font-semibold"
                        : "text-slate-300 hover:bg-brand-navy-light hover:text-white"
                    )}
                  >
                    <item.icon className={cn(
                      "h-5 w-5 transition-colors flex-shrink-0",
                      isActive ? "text-brand-navy" : "text-slate-400 group-hover:text-brand-gold"
                    )} />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1">{item.label}</span>
                        {isActive && <ChevronRight className="h-4 w-4" />}
                      </>
                    )}
                  </NavLink>
                );

                if (isCollapsed) {
                  return (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild>
                        {navContent}
                      </TooltipTrigger>
                      <TooltipContent side="right" className="bg-brand-navy border-brand-navy-light">
                        <p>{item.label}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return navContent;
              })}
            </nav>
          </ScrollArea>

          {/* User Info & Logout */}
          <div className={cn(
            "border-t border-brand-navy-light",
            isCollapsed ? "p-2" : "p-4"
          )}>
            {!isCollapsed && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-brand-navy-light mb-3">
                <div className="w-8 h-8 rounded-full bg-brand-gold flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-brand-navy">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-brand-gold capitalize">{user?.role}</p>
                </div>
              </div>
            )}
            
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    variant="ghost"
                    size="icon"
                    className="w-full text-slate-300 hover:text-white hover:bg-brand-navy-light"
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-brand-navy border-brand-navy-light">
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                onClick={handleLogout}
                data-testid="logout-btn"
                variant="ghost"
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-brand-navy-light"
              >
                <LogOut className="h-4 w-4 mr-3" />
                Logout
              </Button>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className={cn(
          "flex-1 overflow-auto transition-all duration-300 ease-in-out",
          isCollapsed ? "ml-[70px]" : "ml-64"
        )}>
          <div className="p-8">
            {children}
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
};

export default SidebarLayout;
